import requestV2 from '@/service/request-v2'
import { normalizePageResult } from '@/service/adapters/page-result'
import type { PageQuery, PageResult } from '@/types/crud'

export interface StatisticalEventFilters {
  channelId: string | number
  accessTime: string[]
  duration: string | number
  continueAttend: string | number
}

export interface StatisticalEventRecord {
  channelId: string | number
  channelName: string
  pv: number
  uv: number
  ip: number
  duration: number
  continueAttendNum: number
}

export interface StatisticalEventSummary {
  pv: number
  uv: number
  ip: number
}

export interface StatisticalEventChannel {
  rowId: string | number
  eventName: string
}

function buildParams(query: PageQuery<StatisticalEventFilters>) {
  return {
    ...query.filters,
    pageNo: query.pageNo,
    pageSize: query.pageSize
  }
}

function normalizeSummary(payload: unknown): StatisticalEventSummary {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new Error('汇总响应必须是对象')
  }
  const summary = payload as Partial<StatisticalEventSummary>
  if (
    typeof summary.pv !== 'number' ||
    typeof summary.uv !== 'number' ||
    typeof summary.ip !== 'number'
  ) {
    throw new Error('汇总响应缺少有效的 pv、uv 或 ip')
  }
  return summary as StatisticalEventSummary
}

async function requireExportFile(blob: Blob) {
  if (!blob.type.includes('application/json')) return blob
  const payload = JSON.parse(await blob.text()) as { message?: string }
  throw new Error(payload.message || '导出失败')
}

export async function fetchStatisticalEventPage(
  query: PageQuery<StatisticalEventFilters>,
  signal: AbortSignal
): Promise<PageResult<StatisticalEventRecord>> {
  const payload = await requestV2.get<unknown>({
    url: '/main/liveStatisticalEvent/queryByCondition',
    params: buildParams(query),
    signal
  })
  return normalizePageResult<StatisticalEventRecord>(payload)
}

export async function fetchStatisticalEventSummary(
  filters: Partial<StatisticalEventFilters>,
  signal: AbortSignal
): Promise<StatisticalEventSummary> {
  const payload = await requestV2.get<unknown>({
    url: '/main/liveStatisticalDept/queryStatistical',
    params: {
      ...filters,
      pageNo: 1,
      pageSize: 1000
    },
    signal
  })
  return normalizeSummary(payload)
}

export async function fetchStatisticalEventChannels(): Promise<
  StatisticalEventChannel[]
> {
  const payload = await requestV2.get<unknown>({
    url: '/main/customerEvents/queryByCondition',
    params: { pageNo: 1, pageSize: 1000 }
  })
  return normalizePageResult<StatisticalEventChannel>(payload).records
}

export function exportStatisticalEvent(
  query: PageQuery<StatisticalEventFilters>
) {
  return requestV2
    .get<Blob>({
      url: '/main/liveStatisticalEvent/download',
      params: buildParams(query),
      responseType: 'blob'
    })
    .then(requireExportFile)
}
